import type { PayrollRegime } from "@/lib/types"

/**
 * CÓMO SE LLAMA CADA RÉGIMEN LABORAL EN CADA PAÍS.
 *
 * Cesar, sobre el paso «Tu gente y tus contrapartes» de Primeros pasos:
 *
 *   «Acá me ha saltado al parecer algo grande: las personas deberían estar
 *   asociadas a cada sociedad y deberían de cumplir con normativas cada una.
 *   Actualmente cuando cargas no elijes a qué sociedad se carga.»
 *
 * Tenía razón en las dos mitades, y son dos problemas distintos:
 *
 * 1. **La sociedad no se elegía.** `employee` SÍ tiene `entity_id` desde el
 *    principio —y su clave única es `(entity_id, documento)`, o sea que el
 *    modelo siempre supo que una persona pertenece a una sociedad—. Lo que
 *    faltaba estaba arriba: la SPA lo inyectaba en silencio desde la sociedad
 *    seleccionada en la barra. El propio `mapping.ts` lo decía: «SIN
 *    CONTRAPARTE en la SPA: `entity_id` (se inyecta desde la entidad activa)».
 *
 *    Eso convierte el alta de una persona en una operación cuyo destino depende
 *    de un desplegable que está en otra parte de la pantalla y que nadie mira al
 *    rellenar un formulario. Dar de alta a treinta personas en la sociedad
 *    equivocada no da ningún error: da una planilla peruana dentro de la
 *    empresa brasileña, y se descubre al declarar. Ahora la sociedad es un campo
 *    del formulario, obligatorio y el primero de todos.
 *
 * 2. **El régimen era peruano para las cuatro.** Las opciones estaban fijas en
 *    «Planilla» y «Recibo por honorarios (4ta)». La «4ta» es la cuarta categoría
 *    de renta peruana; en São Paulo eso no existe, y quien dé de alta a un
 *    contratista brasileño tiene que elegir entre dos etiquetas que no son de su
 *    país para que el sistema guarde algo.
 *
 * ---------------------------------------------------------------------------
 * LO QUE **NO** SE TOCA: LOS VALORES GUARDADOS
 *
 * El enum del backend es `planilla | recibo_honorarios | tercero`, y se queda
 * igual. Los nombres suenan peruanos pero los CONCEPTOS son los tres que hay en
 * los cuatro países:
 *
 *   planilla           relación de dependencia, con cargas sociales del empleador
 *   recibo_honorarios  profesional independiente, factura sus servicios
 *   tercero            un proveedor, no una persona del equipo
 *
 * Lo que cambia es cómo se LLAMAN en pantalla. Renombrar el enum obligaría a una
 * migración y a reescribir el rulepack de cada país para ganar exactamente nada:
 * el dato que hay que guardar es el mismo, y el nombre correcto depende de quién
 * mira, no de cómo está la columna.
 *
 * ---------------------------------------------------------------------------
 * LO QUE ESTO **NO** RESUELVE, Y HAY QUE DECIRLO
 *
 * Que la etiqueta diga «CLT» no hace que el sistema calcule un FGTS. El cálculo
 * de aportes vive en el rulepack de cada país (`modules/countries/<pais>`) y hoy
 * sólo el peruano está completo: `datos_prevision` guarda AFP/ONP, asignación
 * familiar y SCTR, que son peruanos. Una persona dada de alta en la sociedad
 * brasileña se guarda bien y se clasifica bien; sus cargas no se calculan
 * todavía. Poner el nombre correcto es la mitad del trabajo y es la mitad que
 * evita cargar a alguien bajo una norma que no es la suya.
 */

/** Los tres valores que guarda el backend. `tercero` no se ofrece en el alta. */
export const REGIMENES: PayrollRegime[] = ["planilla", "recibo_honorarios"]

interface TextoRegimen {
  etiqueta: string
  ayuda: string
}

/**
 * Por país y por régimen. `_` es el genérico: se usa cuando la sociedad es de un
 * país que todavía no tiene textos propios.
 *
 * El genérico no dice «Planilla» a secas —eso sería el peruano disfrazado de
 * neutro— sino el concepto: «Relación de dependencia». Alguien de un país sin
 * textos ve una descripción correcta en vez de la etiqueta de otro país.
 */
const TEXTOS: Record<string, Record<PayrollRegime, TextoRegimen>> = {
  _: {
    planilla: {
      etiqueta: "Relación de dependencia",
      ayuda: "Contrato laboral. El empleador retiene y aporta.",
    },
    recibo_honorarios: {
      etiqueta: "Profesional independiente",
      ayuda: "Factura sus servicios. Sin cargas sociales del empleador.",
    },
  },
  PE: {
    planilla: {
      etiqueta: "Planilla (5ta categoría)",
      ayuda: "Quinta categoría. Lleva EsSalud, AFP u ONP, CTS y gratificaciones.",
    },
    recibo_honorarios: {
      etiqueta: "Recibo por honorarios (4ta)",
      ayuda: "Cuarta categoría. Retención del 8 % sobre el monto del recibo.",
    },
  },
  MX: {
    planilla: {
      etiqueta: "Nómina (sueldos y salarios)",
      ayuda: "Lleva IMSS, INFONAVIT y retención de ISR por el patrón.",
    },
    recibo_honorarios: {
      etiqueta: "Honorarios (asimilados o RESICO)",
      ayuda: "Emite CFDI de servicios profesionales. Retención de ISR e IVA.",
    },
  },
  BR: {
    planilla: {
      etiqueta: "CLT",
      ayuda: "Carteira assinada. Lleva INSS, FGTS y 13º salário.",
    },
    recibo_honorarios: {
      etiqueta: "PJ / autônomo",
      ayuda: "Presta servicios por su propia empresa o como RPA.",
    },
  },
  PA: {
    planilla: {
      etiqueta: "Planilla",
      ayuda: "Lleva Seguro Social, décimo tercer mes y prima de antigüedad.",
    },
    recibo_honorarios: {
      etiqueta: "Servicios profesionales",
      ayuda: "Emite recibo. Retención del ISR según el caso.",
    },
  },
}

/** El país que gobierna los nombres, o el genérico. */
function tabla(pais: string | null | undefined) {
  if (!pais) return TEXTOS._
  return TEXTOS[pais.toUpperCase()] ?? TEXTOS._
}

export function etiquetaRegimen(regimen: PayrollRegime, pais: string | null | undefined): string {
  return tabla(pais)[regimen].etiqueta
}

export function ayudaRegimen(regimen: PayrollRegime, pais: string | null | undefined): string {
  return tabla(pais)[regimen].ayuda
}

/**
 * ¿Hay textos propios para este país?
 *
 * Se usa para DECIRLO en pantalla. Un formulario que enseña «Relación de
 * dependencia» donde debería decir «CLT» no está roto, pero quien lo rellena
 * tiene que poder saber que el sistema todavía no conoce la norma de su país en
 * vez de deducirlo de que las etiquetas suenan raras.
 */
export function tieneNormativaPropia(pais: string | null | undefined): boolean {
  return Boolean(pais && TEXTOS[pais.toUpperCase()])
}
